/**
 * TWINT return-URL and deep-link handling for web and Capacitor native.
 *
 * Web: Stripe redirects the browser back to
 *   `${window.location.origin}/app/circles/:id/payment/twint/complete`
 * which the React router handles inline. `onTwintReturn` is a no-op.
 *
 * Native: there is no browser URL to come back to. Instead, Stripe opens the
 * TWINT app (or a Chrome Custom Tab / SFSafariViewController) and the
 * configured `return_url` fires a custom scheme,
 *   `circleup://twint/return?circle_id=:id`
 * which Android's intent filter (AndroidManifest.xml) routes to
 * `MainActivity` with `singleTask`, causing Capacitor's `App` plugin to
 * emit an `appUrlOpen` event. `onTwintReturn` subscribes to that event and
 * invokes the React callback so the polling flow can advance.
 */

import { isNative } from './index'

/**
 * The router pattern for the web return, and the URL we hand Stripe, are one
 * fact — so they are declared once. They used to be two: this module built a
 * URL for a route App.tsx did not define, and because the router had no
 * catch-all, the redirect rendered a blank page. App.tsx imports this constant,
 * so the two cannot drift apart again.
 *
 * Router-relative (no `/app`): BrowserRouter already applies ROUTER_BASENAME.
 */
export const TWINT_RETURN_ROUTE = 'circles/:id/payment/twint/complete'

/** Rails serves the SPA under `/app`; the deep-link branch below bypasses it. */
const WEB_BASENAME = '/app'

const TWINT_PATH = (circleId: string) =>
  `${WEB_BASENAME}/${TWINT_RETURN_ROUTE.replace(':id', encodeURIComponent(circleId))}`

const NATIVE_SCHEME = 'circleup'

// Dues bills return to the association's dues screen, not a circle's.
// Native returns through the `circleup://` deep link the shell already
// registers for TWINT; web returns to the app's own origin.
export function buildDuesTwintReturnUrl(associationId: string, invoiceId: string): string {
  if (isNative()) {
    return `${NATIVE_SCHEME}://twint/return?invoice_id=${encodeURIComponent(invoiceId)}` +
      `&association_id=${encodeURIComponent(associationId)}`
  }
  const origin =
    typeof window !== 'undefined' && window.location ? window.location.origin : ''
  return `${origin}${WEB_BASENAME}/associations?view=member-dues` +
    `&id=${encodeURIComponent(associationId)}&invoice=${encodeURIComponent(invoiceId)}`
}

export function buildTwintReturnUrl(circleId: string): string {
  if (isNative()) {
    return `${NATIVE_SCHEME}://twint/return?circle_id=${encodeURIComponent(circleId)}`
  }
  const origin =
    typeof window !== 'undefined' && window.location ? window.location.origin : ''
  return `${origin}${TWINT_PATH(circleId)}`
}

export interface TwintReturnEvent {
  circleId: string | null
  url: string
}

type Unsubscribe = () => void

/**
 * Subscribe to TWINT deep-link returns. On web this is a no-op because the
 * Stripe redirect already reloads the app at the return URL — React Router
 * handles it. On native, wires into `@capacitor/app` `appUrlOpen`.
 *
 * The App plugin is imported lazily via `await import` so the web bundle
 * does not pay the cost of parsing it and so this module stays testable
 * in jsdom (no Capacitor runtime).
 */
export function onTwintReturn(callback: (event: TwintReturnEvent) => void): Unsubscribe {
  if (!isNative()) return () => {}

  let cleanup: (() => void) | null = null
  let cancelled = false

  void (async () => {
    try {
      const mod = await import(/* @vite-ignore */ '@capacitor/app')
      if (cancelled) return
      const handle = await mod.App.addListener(
        'appUrlOpen',
        (payload: { url: string }) => {
          if (!payload?.url?.startsWith(`${NATIVE_SCHEME}://twint/return`)) return
          let circleId: string | null = null
          try {
            const parsed = new URL(payload.url)
            circleId = parsed.searchParams.get('circle_id')
          } catch {
            /* malformed deep link — surface the raw URL only */
          }
          callback({ circleId, url: payload.url })
        },
      )
      cleanup = () => {
        void handle.remove()
      }
    } catch {
      /* @capacitor/app not available (test or web build) — nothing to do */
    }
  })()

  return () => {
    cancelled = true
    if (cleanup) cleanup()
  }
}
